import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";

const LOGICALUNIT_URL = "U3106";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Réponse GET /U3106/initLU — sans `logicalUnitDescription` côté backend ; titre UI = i18n `title`. */
export interface U3106InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
}

/** Ligne retournée par POST /U3106/search (champs demandés via filter.fields). */
export interface U3106ExpenseTypeSearchRow {
  id: string;
  code?: string;
  description?: string | null;
  version: number;
  factor?: number | null;
  majorFactor?: number;
  status?: number;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
}

/** Si l’API renvoie encore une organisation (rétrocompatibilité) ; GET /U3106/:id ne l’expose plus. */
export interface U3106ExpenseTypeOrganization {
  id: string;
  uuid: string;
  organizationName: string;
}

/** Détail GET /U3106/:id (result) — sans `organization` côté backend actuel. */
export interface U3106ExpenseType {
  id: string;
  code: string;
  description: string | null;
  /** Coefficient (facteur) ; null si non défini côté métier. */
  factor: number | null;
  /** Facteur de majoration ; défaut métier 0. */
  majorFactor: number;
  /** Petit entier (ex. actif/inactif) ; défaut métier 1. */
  status: number;
  version: number;
  organization?: U3106ExpenseTypeOrganization;
}

/**
 * Formulaire : description en string pour les inputs ;
 * `majorFactor` absent sur fiche vierge (obligatoire à la saisie avant enregistrement).
 */
export type U3106ExpenseTypeFormRow = Omit<U3106ExpenseType, "description" | "majorFactor"> & {
  description: string;
  majorFactor?: number;
};

export interface U3106ExpenseTypeCreateBody {
  code: string;
  description?: string | null;
  factor?: number | null;
  majorFactor?: number;
  status?: number;
}

export interface U3106ExpenseTypeUpdateBody {
  id: string;
  code: string;
  description?: string | null;
  version: number;
  factor?: number | null;
  majorFactor?: number;
  status?: number;
}

export interface U3106ExpenseTypeDeleteBody {
  id: string;
  version: number;
}

/** Ligne formulaire vide (création) : pas de `majorFactor` tant que l’utilisateur n’a pas saisi de valeur. */
export function createEmptyU3106FormRow(): U3106ExpenseTypeFormRow {
  return {
    id: "",
    code: "",
    description: "",
    factor: null,
    majorFactor: undefined,
    status: 1,
    version: 0,
  };
}

/** Normalise une ligne API (rétrocompatibilité si champs absents). */
export function normalizeU3106ExpenseTypeFromApi(row: U3106ExpenseType): U3106ExpenseType {
  return {
    ...row,
    factor: row.factor === undefined ? null : row.factor,
    majorFactor: row.majorFactor ?? 0,
    status: row.status ?? 1,
    description: row.description ?? null,
  };
}

/** Convertit la ligne formulaire en modèle API (description vide → null, défauts majorFactor/status). */
export function formRowToApiExpenseType(row: U3106ExpenseTypeFormRow): U3106ExpenseType {
  const majorFactor =
    typeof row.majorFactor === "number" && !Number.isNaN(row.majorFactor) ? row.majorFactor : 0;
  const status = typeof row.status === "number" && !Number.isNaN(row.status) ? row.status : 1;
  return {
    ...row,
    description: row.description.trim() === "" ? null : row.description.trim(),
    factor: row.factor === undefined || row.factor === null ? null : row.factor,
    majorFactor,
    status,
  };
}

export const U3106_ERROR_CODE_DUPLICATE = "BU310601";

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3106ExpenseTypeCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3106ExpenseTypeUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3106ExpenseTypeDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

/**
 * POST create : `code`, `description` et `majorFactor` attendus (validation UI) ;
 * factor absent → omis (null serveur) ; status absent du corps → 1 côté serveur si omis.
 */
export function buildCreateBody(data: U3106ExpenseTypeFormRow): U3106ExpenseTypeCreateBody {
  const m = formRowToApiExpenseType(data);
  const body: U3106ExpenseTypeCreateBody = {
    code: m.code.trim(),
    description: m.description,
    majorFactor: m.majorFactor,
  };
  if (m.factor !== null && m.factor !== undefined) {
    body.factor = m.factor;
  }
  if (m.status !== 1) {
    body.status = m.status;
  }
  return body;
}

/** POST update : id, code, version + champs métier (factor null pour effacer). */
export function buildUpdateBody(data: U3106ExpenseType): U3106ExpenseTypeUpdateBody {
  return {
    id: data.id,
    code: data.code.trim(),
    description: data.description,
    version: data.version,
    factor: data.factor,
    majorFactor: data.majorFactor ?? 0,
    status: data.status ?? 1,
  };
}

export function buildDeleteBody(data: U3106ExpenseType): U3106ExpenseTypeDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3106_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors["code"] = t("errCodeDuplicate", { ns: nameSpace });
      break;
    default:
      break;
  }
}
